import React, { useMemo } from 'react';
import {
    Box,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Chip,
    Typography,
    IconButton,
    Tooltip,
    Button,
    useTheme,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VerifiedIcon from '@mui/icons-material/Verified';
import { formatDistanceToNow } from 'date-fns';
import {
    SessionQuestionEntityFrontend,
    AnswerTypeFrontend,
    QuestionOptionDtoFrontend,
} from '../../../api/sessionQuestionsApi';
import { getQuestionLabel, getActorFriendlyName } from './questionFormConstants';
import ListObjectDisplay from './ListObjectDisplay';

export interface QuestionFieldProps {
    question: SessionQuestionEntityFrontend;
    value: string;
    isDirty: boolean;
    onChange: (value: string) => void;
    onOpenHistory: () => void;
    onOpenListEditor: () => void;
}

const QuestionField: React.FC<QuestionFieldProps> = ({
    question,
    value,
    isDirty,
    onChange,
    onOpenHistory,
    onOpenListEditor,
}) => {
    const theme = useTheme();
    const label = getQuestionLabel(question);
    const isRequired = question.isRequiredAnswer;
    const hasAnswer = value !== '' && value !== undefined && value !== null;

    const renderField = () => {
        switch (question.answerType) {
            case AnswerTypeFrontend.TEXT:
                return (
                    <TextField
                        fullWidth
                        size="small"
                        label={label}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="Enter answer..."
                    />
                );

            case AnswerTypeFrontend.TEXT_AREA:
                return (
                    <TextField
                        fullWidth
                        size="small"
                        label={label}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        multiline
                        rows={3}
                        placeholder="Enter answer..."
                    />
                );

            case AnswerTypeFrontend.NUMBER:
                return (
                    <TextField
                        fullWidth
                        size="small"
                        label={label}
                        type="number"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="Enter number..."
                    />
                );

            case AnswerTypeFrontend.BOOLEAN:
                return (
                    <FormControl fullWidth size="small">
                        <InputLabel id={`bool-${question.id}`}>{label}</InputLabel>
                        <Select
                            labelId={`bool-${question.id}`}
                            label={label}
                            value={value}
                            onChange={(e) => onChange(e.target.value as string)}
                        >
                            <MenuItem value=""><em>Not Set</em></MenuItem>
                            <MenuItem value="true">Yes</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                        </Select>
                    </FormControl>
                );

            case AnswerTypeFrontend.DATE:
                return (
                    <TextField
                        fullWidth
                        size="small"
                        label={label}
                        type="date"
                        value={value ? value.split('T')[0] : ''}
                        onChange={(e) => onChange(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                );

            case AnswerTypeFrontend.RADIO: {
                const optionCount = Array.isArray(question.options) ? question.options.length : 0;
                if (optionCount > 5) {
                    return (
                        <FormControl fullWidth size="small">
                            <InputLabel id={`radio-${question.id}`}>{label}</InputLabel>
                            <Select
                                labelId={`radio-${question.id}`}
                                label={label}
                                value={value}
                                onChange={(e) => onChange(e.target.value as string)}
                            >
                                <MenuItem value=""><em>Not Set</em></MenuItem>
                                {question.options?.map((opt: QuestionOptionDtoFrontend) => (
                                    <MenuItem key={opt.key} value={opt.key}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    );
                }
                return (
                    <FormControl component="fieldset" fullWidth>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{label}</Typography>
                        <RadioGroup
                            row
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                        >
                            {question.options?.map((opt: QuestionOptionDtoFrontend) => (
                                <FormControlLabel
                                    key={opt.key}
                                    value={opt.key}
                                    control={<Radio size="small" />}
                                    label={opt.label}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                );
            }

            case AnswerTypeFrontend.MULTISELECT: {
                let selectedValues: string[] = [];
                if (typeof value === 'string' && value.startsWith('[')) {
                    try { selectedValues = JSON.parse(value); } catch { selectedValues = []; }
                }
                return (
                    <FormControl fullWidth size="small">
                        <InputLabel id={`multi-${question.id}`}>{label}</InputLabel>
                        <Select
                            labelId={`multi-${question.id}`}
                            label={label}
                            multiple
                            value={selectedValues}
                            onChange={(e) => {
                                const vals = e.target.value as string[];
                                onChange(JSON.stringify(vals));
                            }}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {(selected as string[]).map(key => {
                                        const opt = question.options?.find(o => o.key === key);
                                        return <Chip key={key} label={opt ? opt.label : key} size="small" />;
                                    })}
                                </Box>
                            )}
                        >
                            {question.options?.map((opt: QuestionOptionDtoFrontend) => (
                                <MenuItem key={opt.key} value={opt.key}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );
            }

            case AnswerTypeFrontend.FILE:
                return (
                    <Box sx={{ p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {value ? 'File Uploaded' : 'No file uploaded'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            File editing is not available here.
                        </Typography>
                    </Box>
                );

            case AnswerTypeFrontend.LIST_OBJECT:
                return (
                    <ListObjectDisplay
                        label={label}
                        value={value}
                        schema={question.listItemSchema}
                        onEdit={onOpenListEditor}
                        editDisabled={!question.listItemSchema}
                    />
                );

            default:
                return (
                    <TextField
                        fullWidth
                        size="small"
                        label={label}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="Enter answer..."
                    />
                );
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                py: 1.5,
                px: 2,
                borderRadius: 1,
                borderLeft: isDirty ? `3px solid ${theme.palette.warning.main}` : '3px solid transparent',
                backgroundColor: isDirty
                    ? (theme.palette.mode === 'dark' ? 'rgba(255, 167, 38, 0.06)' : 'rgba(255, 167, 38, 0.04)')
                    : 'transparent',
                transition: 'background-color 0.2s, border-color 0.2s',
            }}
        >
            {/* Status indicator */}
            <Box sx={{ pt: 1, flexShrink: 0 }}>
                {hasAnswer ? (
                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                ) : (
                    <ErrorOutlineIcon color={isRequired ? 'warning' : 'disabled'} fontSize="small" />
                )}
            </Box>

            {/* Field + metadata */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Badges row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    {isRequired && !hasAnswer && (
                        <Chip label="Required" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                    )}
                    {!isRequired && !hasAnswer && (
                        <Chip label="Optional" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                    )}
                    {question.requiresVerification && (
                        <Tooltip title="This answer requires verification">
                            <VerifiedIcon color="info" sx={{ fontSize: '1rem' }} />
                        </Tooltip>
                    )}
                    {isDirty && (
                        <Chip label="Modified" size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
                    )}
                </Box>

                {/* The form field */}
                {renderField()}

                {/* Metadata row */}
                {question.answeredAt && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                            Answered {formatDistanceToNow(new Date(question.answeredAt), { addSuffix: true })}
                        </Typography>
                        {question.lastAnsweredByActorType && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {getActorFriendlyName(question.lastAnsweredByActorType, question.lastAnsweredByActorId).icon}
                                <Typography variant="caption" color="text.secondary">
                                    {getActorFriendlyName(question.lastAnsweredByActorType, question.lastAnsweredByActorId).name}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* History button */}
            <Box sx={{ pt: 0.5, flexShrink: 0 }}>
                <Tooltip title="View history">
                    <IconButton size="small" onClick={onOpenHistory}>
                        <HistoryIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
};

export default QuestionField;
